import { asText } from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import { useRef } from 'react';
import { Button } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import HeaderContext from 'remote-com-shared/components/Header/context';
import MenuEntry from 'remote-com-shared/components/HeaderNavigation/MenuEntry';
import MenuItem from 'remote-com-shared/components/HeaderNavigation/MenuItem';
import {
  NavigationPrimary,
  NavigationSecondary,
} from 'remote-com-shared/components/HeaderNavigation/styles/Navigation';
import { Hero as HeroComponent } from 'remote-com-shared/components/Hero';
import { Main, Title, Description } from 'remote-com-shared/components/Hero/styles';
import { fieldHasContent } from 'remote-com-shared/domains/prismic/helpers';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import { CTAButton } from '../sharedStyles';

import { transformProperties } from '@/src/domains/prismic/helpers';
import { desktopBreakpoint, fromTabletBreakpoint } from '@/src/domains/shared/breakpoints';
import { Header } from '@/src/views/SharedEvents/Header';

const Hero = styled(HeroComponent)`
  background-image: unset;
  background-color: var(--color-tone-primary);
  position: relative;
  padding-top: 0;
  z-index: 3;

  ${Title} {
    ${fluidProp('font-size', '34px', '48px')}
    font-family: var(--typography-fontBossa);
    font-weight: 700;
    line-height: 1.2;
    max-width: 850px;
    width: 40%;

    ${desktopBreakpoint(css`
      width: 100%;
    `)}

    @media only screen and (min-width: 1650px) {
      width: 60%;
    }
    @media only screen and (min-width: 2300px) {
      width: 100%;
    }
  }

  ${ContentContainer} {
    position: unset;
  }

  ${Main} {
    z-index: 1;
    min-height: 800px;
  }

  ${Description} {
    width: 40%;
    font-size: 20px;
    color: var(--colors-blank);

    ${desktopBreakpoint(css`
      width: 100%;
    `)}
    @media only screen and (min-width: 1650px) {
      width: 60%;
    }
    @media only screen and (min-width: 2300px) {
      width: 100%;
    }
  }

  ${fromTabletBreakpoint(css`
    clip-path: ellipse(120% 100% at 50% 0%);
  `)}
`;

const BgIllustrationWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  max-width: 60%;
  margin: 0;
  display: flex;
  align-items: center;

  ${desktopBreakpoint(css`
    display: none;
  `)}
`;

const NavButton = styled(Button)`
  && {
    border-color: var(--color-tone-secondary);
    background: var(--color-tone-primary);

    &:hover,
    &:focus {
      background: var(--color-tone-secondary);
      color: var(--color-tone-primary);
    }
  }
`;

export function HeroSlice({ slice, context }) {
  const { primary } = transformProperties(slice);
  const { sectionId, title, description, bgImage, ctaLabel, ctaLink } = primary;
  const { headerNavigationItems } = context;
  const headerRef = useRef(null);

  return (
    <>
      <Header ref={headerRef} hideBanner useRemoteFont>
        <NavigationPrimary>
          {headerNavigationItems.map(({ label, fragment }, index) => (
            <MenuEntry key={index}>
              <MenuItem as="a" href={fragment}>
                {label}
              </MenuItem>
            </MenuEntry>
          ))}
        </NavigationPrimary>
        <NavigationSecondary>
          {ctaLink && <NavButton href={ctaLink?.url}>{ctaLabel}</NavButton>}
        </NavigationSecondary>
      </Header>
      <HeaderContext.Provider value={headerRef}>
        <Hero
          title={<PrismicRichText field={title} />}
          description={fieldHasContent(description) && asText(description)}
          actions={ctaLabel && <CTAButton href={ctaLink?.url}>{ctaLabel}</CTAButton>}
          preview="0vh"
          id={sectionId}
          background={
            <BgIllustrationWrapper>
              <Image src={bgImage.url} {...bgImage.dimensions} alt={bgImage.alt || ''} />
            </BgIllustrationWrapper>
          }
        />
      </HeaderContext.Provider>
    </>
  );
}
